package xim.poc.game.configuration.v0.tactics

import xim.poc.ActorId
import xim.poc.audio.AudioManager
import xim.poc.audio.SystemSound
import xim.poc.game.ActorState
import xim.poc.game.ActorStateManager
import xim.poc.game.AttackContext
import xim.poc.game.configuration.constants.SkillId
import xim.poc.game.configuration.v0.HelpNotifier
import xim.poc.ui.ChatLog
import xim.util.FrameTimer
import kotlin.math.sign
import kotlin.time.Duration
import kotlin.time.Duration.Companion.ZERO
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

class TurnLock(var locked: Boolean = true)

class ActionBuilder {
    var actorId: ActorId? = null
    var destination: CursorPosition? = null
    var skill: SkillSelectChoice? = null
    var targetId: ActorId? = null

    fun reset() {
        actorId = null
        destination = null
        skill = null
        targetId = null
    }

}

class Action(val source: ActorState, val skillId: SkillId, val target: ActorState)

enum class MatchConclusion {
    Victory,
    Defeat,
    Exit,
}

sealed interface TacticsMatchState {

    fun update(elapsedFrames: Float)

    fun isComplete(): Boolean

    fun onComplete() {}

}

class TacticsMatchSetup: TacticsMatchState {
    var completed = false

    override fun update(elapsedFrames: Float) { }

    override fun isComplete(): Boolean {
        return completed
    }

}

class TacticsMatchConcluded(val match: TacticsMatchInstance, val conclusion: MatchConclusion): TacticsMatchState {

    private val matchEndTimer = when (conclusion) {
        MatchConclusion.Victory -> FrameTimer(4.seconds)
        MatchConclusion.Defeat -> FrameTimer(3.seconds)
        MatchConclusion.Exit -> FrameTimer(ZERO)
    }

    private var displayed = false

    override fun update(elapsedFrames: Float) {
        display()
        matchEndTimer.update(elapsedFrames)
    }

    override fun isComplete(): Boolean {
        return matchEndTimer.isReady()
    }

    private fun display() {
        if (displayed) { return }
        displayed = true

        when (conclusion) {
            MatchConclusion.Victory -> {
                match.config.onVictory(match)
                HelpNotifier.notify("Victory!", 3.seconds)
                AudioManager.playSystemSoundEffect(SystemSound.QuestComplete)
            }
            MatchConclusion.Defeat -> {
                HelpNotifier.notify("Defeated...", 3.seconds)
            }
            MatchConclusion.Exit -> {
                HelpNotifier.notify("Exited the match!", 2.seconds)
            }
        }
    }

}

class TacticsMatchUnitTurn(val match: TacticsMatchInstance, val unit: ActorState): TacticsMatchState {

    var totalTurnTime: Float = 0f
    val actionBuilder = ActionBuilder()

    private var action: Action? = null
    private var turnEndWait: FrameTimer? = null
    private val locks = ArrayList<TurnLock>()
    private var knockbackCoordinator: KnockbackCoordinator? = null

    override fun update(elapsedFrames: Float) {
        totalTurnTime += elapsedFrames
        turnEndWait?.update(elapsedFrames)
    }

    override fun isComplete(): Boolean {
        return turnEndWait?.isReady() == true && locks.none { it.locked }
    }

    override fun onComplete() {
        consumeStatusEffects()
    }

    fun getSubmittedAction(): Action? {
        return action
    }

    fun onSkillExecuted() {
        turnEndWait = FrameTimer(1.seconds)
    }

    fun onSkillAnimationComplete(endWait: Duration) {
        turnEndWait = FrameTimer(endWait)
    }

    fun hasExecutedSkill(): Boolean {
        return turnEndWait != null
    }

    fun hasSubmittedAction(): Boolean {
        return action != null || hasExecutedSkill()
    }

    fun lock(): TurnLock {
        return TurnLock().also { locks += it }
    }

    fun isEnemyTurn(): Boolean {
        return unit.isEnemy()
    }

    fun submitAction() {
        val sourceId = actionBuilder.actorId
        val source = ActorStateManager[sourceId] ?: throw IllegalStateException("Action source not specified")

        val targetId = actionBuilder.targetId
        val target = ActorStateManager[targetId] ?: throw IllegalStateException("Action target not specified")

        val destination = actionBuilder.destination ?: throw IllegalStateException("Action destination not specified")
        val skill = actionBuilder.skill as? SkillChoice ?: throw IllegalStateException("Action skill not specified")

        action = Action(source, skill.skillId, target)
        source.setCursorPosition(destination)
        source.setMovementCommand(destination.toWorldSpace())
        source.setMostRecentTurn(match.getCurrentTurnCount())

        match.onTurnSubmitted()
    }

    fun submitPositionSkill() {
        val sourceId = actionBuilder.actorId
        val source = ActorStateManager[sourceId] ?: throw IllegalStateException("Action source not specified")

        val targetId = actionBuilder.targetId
        val target = ActorStateManager[targetId] ?: throw IllegalStateException("Action target not specified")

        val destination = actionBuilder.destination ?: throw IllegalStateException("Action destination not specified")
        val skill = actionBuilder.skill as? PositionChoice ?: throw IllegalStateException("Action skill not specified")

        turnEndWait = FrameTimer(0.5.seconds)
        skill.positionSkill.apply(match, this, source, target)
        source.consumeTp(skill.positionSkill.tpCost())
        source.setMostRecentTurn(match.getCurrentTurnCount())

        match.onTurnSubmitted()
    }

    fun submitWait(tpCost: Int = 200, delay: Duration = ZERO) {
        val sourceId = actionBuilder.actorId
        val source = ActorStateManager[sourceId] ?: throw IllegalStateException("Wait source not specified")

        val destination = actionBuilder.destination ?: throw IllegalStateException("Wait destination not specified")

        turnEndWait = FrameTimer(delay)

        ChatLog("${source.name} waited.")
        source.consumeTp(tpCost)

        source.setCursorPosition(destination)
        source.setMovementCommand(destination.toWorldSpace())
        source.setMostRecentTurn(match.getCurrentTurnCount())

        match.onTurnSubmitted()
    }

    fun knockback(defender: ActorState, drawIn: Boolean, context: AttackContext?) {
        val current = knockbackCoordinator ?: KnockbackCoordinator(unit, drawIn, match).also { knockbackCoordinator = it }
        current.knockback(defender, context)
    }

    private fun consumeStatusEffects() {
        for (effect in unit.getStatusEffects()) {
            if (!TacticsStatusHelper.shouldCountDown(effect)) { continue }
            if (effect.secondaryCounter == match.getCurrentTurnCount()) { continue }
            unit.consumeStatusEffectCharge(effect.statusEffect, expireOnZero = true)
        }
    }

}

private class KnockbackCoordinator(val attacker: ActorState, val drawIn: Boolean, val match: TacticsMatchInstance) {

    private val pending = ArrayList<Pair<ActorState, AttackContext?>>()

    fun knockback(defender: ActorState, context: AttackContext?) {
        val knockbackSuccess = performKnockback(defender, context)
        if (!knockbackSuccess) {
            pending += (defender to context)
            return
        }

        // A different unit was successfully knocked back - try again on units that were previously blocked
        val itr = pending.iterator()
        while (itr.hasNext()) {
            val (retryActor, retryContext) = itr.next()
            if (!performKnockback(retryActor, retryContext)) { continue }
            itr.remove()
        }
    }

    fun performKnockback(defender: ActorState, context: AttackContext?): Boolean {
        val destination = getKnockbackPosition(attacker, defender, drawIn) ?: return false
        defender.setCursorPosition(destination)

        AttackContext.compose(context) {
            defender.setMovementCommand(destination.toWorldSpace())
            defender.frozenTimer.reset(500.milliseconds)
        }

        return true
    }

    private fun getKnockbackPosition(attacker: ActorState, defender: ActorState, drawIn: Boolean): PanelPosition? {
        val sourcePosition = match.getUnitPosition(attacker) as? PanelPosition ?: return null
        val targetPosition = match.getUnitPosition(defender) as? PanelPosition ?: return null

        val (dX, dY) = if (drawIn) {
            (sourcePosition.x - targetPosition.x).sign to (sourcePosition.y - targetPosition.y).sign
        } else {
            (targetPosition.x - sourcePosition.x).sign to (targetPosition.y - sourcePosition.y).sign
        }

        val destination = PanelPosition.maybe(targetPosition.x + dX, targetPosition.y + dY)
            ?: PanelPosition.maybe(targetPosition.x + dX, targetPosition.y)
            ?: PanelPosition.maybe(targetPosition.x, targetPosition.y + dY)
            ?: return null

        if (match.getUnit(destination) != null) { return null }
        return destination
    }

}